import { Component, ElementRef, NgZone, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { LoginService } from '@app/service/login.service';
import { AiChatService, AiChatEvent, ChatTurn, Respuesta, RespuestaGrafico, Conversacion } from '@app/service/ai-chat.service';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  html?: string;
  error?: boolean;
  payload?: Respuesta;
  chartData?: any;
  chartOptions?: any;
  messageId?: number;
  feedback?: 1 | -1;
}

@Component({
  selector: 'app-ai-chat',
  templateUrl: './ai-chat.component.html',
  styleUrls: ['./ai-chat.component.css']
})
export class AiChatComponent {

  open = false;
  /** Nombre de marca (no hardcodeado): sale de environment.company. */
  companyName = environment.company;
  messages: ChatMessage[] = [];
  input = '';
  thinking = false;
  toolStatus: string | null = null;
  conversationId: number | null = null;
  conversaciones: Conversacion[] = [];
  showHistory = false;

  suggestions = [
    '¿Cuánto vendí este mes?',
    '¿Cuánto me deben mis clientes?',
    'Resumen de caja de la semana',
    '¿Qué categorías vendo más?'
  ];

  @ViewChild('scrollArea') scrollArea?: ElementRef<HTMLDivElement>;

  constructor(
    private aiChat: AiChatService,
    private zone: NgZone,
    private router: Router,
    private loginService: LoginService
  ) { }

  /** El usuario solo puede usar la IA si tiene este permiso. */
  get puedeUsarIa(): boolean {
    return this.loginService.hasPermission('PERMITIR_CONSULTAS_IA') || this.loginService.hasPermission('SUPER_USUARIO');
  }

  toggle(): void {
    this.open = !this.open;
    if (this.open) {
      // Sin permiso: el panel se abre pero muestra el bloqueo; no cargamos nada ni llamamos a la IA.
      if (!this.puedeUsarIa) {
        return;
      }
      this.cargarConversaciones();
      // Al abrir sin conversación activa, muestra proactivamente el resumen.
      if (!this.messages.length && !this.thinking) {
        this.send('Salúdame por mi nombre según la hora del día y dame un breve resumen de cómo va mi negocio hoy', true);
      } else {
        // El panel se recrea al reabrir (*ngIf): reposiciona el scroll al final.
        this.scrollToBottom();
      }
    }
  }

  sendSuggestion(suggestion: string): void {
    this.send(suggestion);
  }

  private cargarConversaciones(): void {
    this.aiChat.listConversations().then(list => this.zone.run(() => (this.conversaciones = list)));
  }

  toggleHistory(): void {
    this.showHistory = !this.showHistory;
    if (this.showHistory) {
      this.cargarConversaciones();
    }
  }

  /** Empieza una conversación nueva (limpia el chat y vuelve a mostrar el resumen). */
  nuevaConversacion(): void {
    this.messages = [];
    this.conversationId = null;
    this.showHistory = false;
    this.send('Salúdame por mi nombre según la hora del día y dame un breve resumen de cómo va mi negocio hoy', true);
  }

  /** Abre una conversación pasada y reconstruye sus mensajes. */
  async abrirConversacion(id: number): Promise<void> {
    this.showHistory = false;
    if (id === this.conversationId) {
      return;
    }
    const mensajes = await this.aiChat.getConversation(id);
    this.zone.run(() => {
      this.conversationId = id;
      this.messages = mensajes.map(m => {
        if (m.role === 'user') {
          return { role: 'user', content: m.content?.texto ?? '' } as ChatMessage;
        }
        const r: Respuesta = m.content ?? { texto: '' };
        return {
          role: 'assistant',
          content: r.texto ?? '',
          html: this.renderMarkdown(r.texto ?? ''),
          payload: r,
          chartData: r.grafico ? this.buildChartData(r.grafico) : undefined,
          chartOptions: r.grafico ? this.chartOptions(r.grafico) : undefined,
          messageId: m.id
        } as ChatMessage;
      });
      this.scrollToBottom();
    });
  }

  darFeedback(msg: ChatMessage, rating: 1 | -1): void {
    if (!msg.messageId || msg.feedback) {
      return;
    }
    msg.feedback = rating;
    this.aiChat.sendFeedback(msg.messageId, rating).then(ok => {
      if (!ok) {
        this.zone.run(() => (msg.feedback = undefined));
      }
    });
  }

  async send(explicit?: string, hidden = false): Promise<void> {
    // Sin permiso no se envía nada a la IA (el panel muestra el bloqueo).
    if (!this.puedeUsarIa) {
      return;
    }
    const message = (explicit ?? this.input).trim();
    if (!message || this.thinking) {
      return;
    }
    if (!explicit) {
      this.input = '';
    }
    // En modo proactivo (hidden) no mostramos la burbuja del usuario.
    if (!hidden) {
      this.messages.push({ role: 'user', content: message });
    }
    this.thinking = true;
    this.toolStatus = null;
    this.scrollToBottom();

    // Historial acotado: últimos 10 turnos sin errores (la persistencia real es Fase 1)
    const history: ChatTurn[] = this.messages
      .slice(0, -1)
      .filter(m => !m.error)
      .slice(-10)
      .map(m => ({ role: m.role, content: m.content }));

    try {
      // El resumen proactivo (hidden) no se persiste, para no ensuciar el historial.
      for await (const event of this.aiChat.chat(message, history, this.conversationId, !hidden)) {
        this.zone.run(() => this.handleEvent(event));
      }
    } catch {
      this.zone.run(() => {
        this.messages.push({
          role: 'assistant',
          content: 'No pude conectarme con el asistente. Inténtalo de nuevo en unos segundos.',
          error: true
        });
      });
    } finally {
      this.zone.run(() => {
        this.thinking = false;
        this.toolStatus = null;
        this.scrollToBottom();
        this.cargarConversaciones();
      });
    }
  }

  private handleEvent(event: AiChatEvent): void {
    if (event.type === 'tool') {
      this.toolStatus = this.friendlyToolName(event.name);
    } else if (event.type === 'respuesta') {
      this.toolStatus = null;
      const r = event.data;
      this.messages.push({
        role: 'assistant',
        content: r.texto,
        html: this.renderMarkdown(r.texto),
        payload: r,
        chartData: r.grafico ? this.buildChartData(r.grafico) : undefined,
        chartOptions: r.grafico ? this.chartOptions(r.grafico) : undefined
      });
    } else if (event.type === 'text') {
      this.toolStatus = null;
      this.messages.push({
        role: 'assistant',
        content: event.content,
        html: this.renderMarkdown(event.content)
      });
    } else if (event.type === 'error') {
      this.toolStatus = null;
      this.messages.push({ role: 'assistant', content: event.message, error: true });
    } else if (event.type === 'meta') {
      this.conversationId = event.conversationId;
    } else if (event.type === 'saved') {
      // Adjunta el id de mensaje a la última respuesta del asistente (para feedback).
      for (let i = this.messages.length - 1; i >= 0; i--) {
        if (this.messages[i].role === 'assistant' && !this.messages[i].messageId) {
          this.messages[i].messageId = event.messageId;
          break;
        }
      }
    }
    this.scrollToBottom();
  }

  /** Navega al módulo real desde un botón de acción y cierra el panel. */
  irAAccion(ruta: string): void {
    this.open = false;
    this.router.navigateByUrl(ruta);
  }

  private readonly chartColors = ['#0a84ff', '#bf5af2', '#ff375f', '#ff9f0a', '#64d2ff', '#30d158'];

  private buildChartData(g: RespuestaGrafico): any {
    const isPie = g.tipo === 'pie';
    return {
      labels: g.etiquetas,
      datasets: g.series.map((s, i) => ({
        label: s.nombre,
        data: s.datos,
        backgroundColor: isPie
          ? g.etiquetas.map((_, j) => this.chartColors[j % this.chartColors.length])
          : this.chartColors[i % this.chartColors.length],
        borderColor: this.chartColors[i % this.chartColors.length],
        borderWidth: g.tipo === 'line' ? 2 : 0,
        fill: false,
        tension: 0.35
      }))
    };
  }

  private chartOptions(g: RespuestaGrafico): any {
    const showLegend = g.tipo === 'pie' || g.series.length > 1;
    return {
      plugins: { legend: { display: showLegend, labels: { boxWidth: 12, font: { size: 10 } } } },
      scales: g.tipo === 'pie' ? {} : {
        x: { ticks: { font: { size: 10 } }, grid: { display: false } },
        y: { ticks: { font: { size: 10 } }, beginAtZero: true }
      },
      maintainAspectRatio: false,
      responsive: true
    };
  }

  private friendlyToolName(name: string): string {
    if (name.startsWith('ventas')) {
      return 'Consultando tus ventas…';
    }
    if (name.startsWith('cuentas_por_cobrar')) {
      return 'Revisando tus cuentas por cobrar…';
    }
    if (name.startsWith('cuentas_por_pagar')) {
      return 'Revisando tus cuentas por pagar…';
    }
    if (name.startsWith('caja')) {
      return 'Consultando tu caja…';
    }
    return 'Consultando tus datos…';
  }

  /** Render mínimo y seguro: escapa HTML y aplica solo negritas, listas y saltos */
  private renderMarkdown(text: string): string {
    const escaped = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return escaped
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.+)$/gm, '<span class="ai-li">$1</span>')
      .replace(/\n/g, '<br>');
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.scrollArea?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
